import {computed, ref} from "vue";
import {debounce} from "lodash";

export const useVideoRecorder = () => {
  let recordingTimeout;
  let mediaRecorder;
  let recordedBlobs;

  const mimeTypes = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm;codecs=vp9",
    "video/webm;codecs=vp8",
    "video/webm",
    "video/mp4",
  ];

  let props;
  let emits;

  const supports = navigator.mediaDevices.getSupportedConstraints();

  const elapsedSeconds = ref(0);
  const maxSeconds = ref(60);
  const recordButton = ref(null);
  const playButton = ref(null);
  const hasRecording = ref(false);
  const isRecording = ref(false);
  const isPlaying = ref(false);
  const hasPermissions = ref(false);
  const autoplayEnabled = ref(true);
  const facingMode = ref("user");
  const fileInput = ref(null);

  const gumVideo = ref(null);
  const recordedVideo = ref(null);

  const countdownTimeout = () => setTimeout(() => {
    if (!isRecording.value) return;

    if (elapsedSeconds.value < maxSeconds.value) {
      elapsedSeconds.value += 1;
      countdownTimeout();
      return;
    }

    stopRecording();
  }, 1000);

  const countdown = () => {
    if (elapsedSeconds.value >= maxSeconds.value) elapsedSeconds.value = 0;
    countdownTimeout();
  };

  const handleDataAvailable = (event) => {
    if (event.data && event.data.size > 0) {
      recordedBlobs.push(event.data);
    }
  };

  const openFileInput = () => fileInput.value.click();

  const setFile = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.type.includes("video")) {
      toast.error("Please select a video file.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const arrayBuffer = event.target.result;
      recordedBlobs = [new Blob([arrayBuffer], {type: file.type})];
      hasRecording.value = true;
      setPlayContent(file.type);
    };

    reader.readAsArrayBuffer(file);
  };

  const getSupportedMimeTypes = () => {
    return mimeTypes.filter((mimeType) => MediaRecorder.isTypeSupported(mimeType));
  };

  const toMMSS = (secondsInt) => {
    let hours = Math.floor(secondsInt / 3600);
    let minutes = Math.floor((secondsInt - hours * 3600) / 60);
    let seconds = secondsInt - hours * 3600 - minutes * 60;

    if (seconds < 10) {
      seconds = "0" + seconds;
    }
    return minutes + ":" + seconds;
  };

  const timeRemainingReadable = computed(() => {
    return toMMSS(elapsedSeconds.value) + " / " + toMMSS(maxSeconds.value);
  });

  const startRecording = () => {
    recordedBlobs = [];
    const supportedMimeTypes = getSupportedMimeTypes();
    const allOptions = [{}, ...supportedMimeTypes.map((mimeType) => ({mimeType}))];

    const attemptStart = (stream) => {
      let recorder = null;
      let error = null;

      for (const options of allOptions) {
        try {
          recorder = new MediaRecorder(stream, options);
          recorder.ondataavailable = handleDataAvailable;
          recorder.start(1000);
          return {recorder, error: null};
        } catch (e) {
          error = e;
        }
      }

      return {recorder: null, error};
    };

    let {recorder, error} = attemptStart(window.stream);

    if (!recorder && window.stream) {
      const videoTracks = window.stream.getVideoTracks();
      if (videoTracks.length) {
        const videoOnlyStream = new MediaStream(videoTracks);
        ({recorder, error} = attemptStart(videoOnlyStream));
        if (recorder) {
          toast.warning("Audio recording is not supported on this browser/device, recording video only.");
        }
      }
    }

    if (!recorder) {
      console.error("Exception while creating/starting MediaRecorder:", error);
      toast.error("Your browser doesn't support recording videos with audio on this device. Try uploading a video file instead.");
      hasPermissions.value = false;
      return;
    }

    mediaRecorder = recorder;
    hasPermissions.value = true;

    isRecording.value = true;
    if (playButton.value) {
      playButton.value.disabled = true;
    }
    countdown();
  };

  const stopRecording = () => {
    if (!mediaRecorder || mediaRecorder.state !== "recording") {
      return;
    }

    mediaRecorder.stop();
    elapsedSeconds.value = 0;
    isRecording.value = false;
    if (playButton.value) {
      playButton.value.disabled = false;
    }
    hasRecording.value = true;
    setTimeout(() => {
      setPlayContent(mediaRecorder.mimeType);
    }, 100);
  };

  const handleSuccess = (stream) => {
    hasPermissions.value = true;
    if (recordButton.value) recordButton.value.disabled = false;
    window.stream = stream;

    if (!gumVideo.value) return;
    gumVideo.value.srcObject = stream;
  };

  const initStream = async (constraints) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      handleSuccess(stream);
    } catch (e) {
      console.error("navigator.getUserMedia error:", e);
      hasPermissions.value = false;
    }
  };

  const vw = () => window.innerWidth || document.documentElement.clientWidth || 0;
  const vh = () => window.innerHeight || document.documentElement.clientHeight || 0;

  let hasStartedStream = false;
  const startStream = async () => {
    hasStartedStream = true;
    const constraints = {
      audio: {
        echoCancellation: {ideal: true},
      },
      video: {
        width: vw(),
        height: vh(),
        facingMode: {ideal: facingMode.value},
      },
    };
    await initStream(constraints);
  };

  const toggleFacingModeSupported = !!supports["facingMode"];
  const toggleFacingMode = async () => {
    if (!toggleFacingModeSupported) {
      toast.warning("You don't have a backwards facing camera on this device.");
      return;
    }

    if (facingMode.value === "user") facingMode.value = "environment";
    else facingMode.value = "user";

    stopAllTracks();
    await startStream();
  };

  const toggleIsRecording = () => {
    clearTimeout(recordingTimeout);
    if (!isRecording.value) startRecording();
    else stopRecording();
  };

  const blobToFile = (theBlob, fileName) => {
    return new File([theBlob], fileName, {
      type: theBlob.type,
      lastModified: Date.now(),
    });
  };

  const setPlayContent = async (mimeType) => {
    recordedVideo.value.volume = 0.5;
    recordedVideo.value.src = null;
    recordedVideo.value.srcObject = null;
    const resolvedMimeType = mimeType || mediaRecorder?.mimeType || "video/webm";
    const superBuffer = new Blob(recordedBlobs, {type: resolvedMimeType});
    recordedVideo.value.src = window.URL.createObjectURL(superBuffer);
    const extension = resolvedMimeType.startsWith("video/") ? resolvedMimeType.replace("video/", ".") : ".webm";
    emits("update:file", blobToFile(superBuffer, "video" + extension));
    emits("update:path", recordedVideo.value.src);
    await togglePlay();
  };

  const togglePlay = async () => {
    if (isPlaying.value) {
      isPlaying.value = false;
      recordedVideo.value.pause();
    } else {
      await attemptAutoplay();
    }
  };

  const attemptAutoplay = async () => {
    try {
      await recordedVideo.value.play();
      autoplayEnabled.value = true;
      isPlaying.value = true;
    } catch (e) {
      console.error("Autoplay with audio is disabled:", e);
      autoplayEnabled.value = false;
      isPlaying.value = false;
      console.info("Autoplay with audio is disabled");
    }
  };

  const reRecord = async () => {
    isPlaying.value = false;
    if (typeof recordedVideo.value.stop === "function") {
      recordedVideo.value.stop();
    }
    hasRecording.value = false;
    if (!hasStartedStream) {
      await startStream();
    }
  };

  const getFile = async () => {
    const mimeType = mediaRecorder?.mimeType || "video/webm";
    return new Blob(recordedBlobs, {type: mimeType});
  };

  const save = async (close = false) => {
    if (recordedBlobs && recordedBlobs.length) emits("save", getFile());
    if (close) closeRecorder();
  };

  const closeRecorder = () => emits("close");

  const init = async (containerProps, containerEmits) => {
    props = containerProps;
    emits = containerEmits;

    hasRecording.value = false;
    maxSeconds.value = props.maxSeconds;

    recordButton.value.addEventListener("click", toggleIsRecording);
    await startStream();
  };

  const stopAllTracks = () => {
    if (window.stream) window.stream.getTracks().forEach((track) => track.stop());
    hasStartedStream = false;
  };

  const debouncedStartStream = debounce(async () => {
    if (isRecording.value) return;
    stopAllTracks();
    await startStream();
  }, 300);

  const handlePlay = () => isPlaying.value = true;
  const handlePause = () => isPlaying.value = false;

  return {
    elapsedSeconds,
    recordButton,
    playButton,
    hasRecording,
    isRecording,
    isPlaying,
    gumVideo,
    recordedVideo,
    hasPermissions,
    autoplayEnabled,
    timeRemainingReadable,
    props,
    fileInput,
    toggleFacingModeSupported,
    debouncedStartStream,
    handlePlay,
    handlePause,
    toggleIsRecording,
    togglePlay,
    reRecord,
    startStream,
    getFile,
    init,
    save,
    closeRecorder,
    toggleFacingMode,
    openFileInput,
    setFile,
    stopAllTracks,
  };
};
